import { useCallback, useState } from "react";
import type { BaseSyntheticEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { ApiError, PreconditionFailedError } from "@/lib/api/errors";
import { applyProblemToForm, surfaceProblem } from "./problem-mapping";

export interface UseFormSubmitOptions<
  TFieldValues extends FieldValues,
  TTransformedValues,
  TResult,
> {
  /** Runs after a successful mutation (awaited before `isPending` clears). */
  onSuccess?: (result: TResult, values: TTransformedValues) => void | Promise<void>;
  /** Wire field name → form field path for API errors (see applyProblemToForm). */
  fieldMap?: Record<string, FieldPath<TFieldValues>>;
  /**
   * Take over GLOBAL error surfacing (e.g. a login form rendering
   * `auth.invalid_credentials` inline instead of a toast). When provided, the
   * kit still lands field-level `errors[]` on the form and still handles 412
   * conflicts via the dialog (`onError` is not called for those), but raises
   * no toast — surfacing everything else is the caller's job.
   */
  onError?: (error: unknown) => void;
}

export interface UseFormSubmitReturn {
  /** Attach to `<form onSubmit>`; validates, mutates, and surfaces errors. */
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  isPending: boolean;
  /** True after a 412 — render `<ConflictDialog open={conflict} …>`. */
  conflict: boolean;
  dismissConflict: () => void;
}

/**
 * The standard submit pipeline: react-hook-form validation → TanStack
 * mutation → the ONE error convention.
 *
 * - `PreconditionFailedError` (412) sets `conflict` for `<ConflictDialog>`;
 * - `ApiError` with field `errors[]` lands on the form via
 *   `applyProblemToForm` (unmapped leftovers become a toast);
 * - everything else raises a destructive toast via `surfaceProblem`.
 */
export function useFormSubmit<
  TFieldValues extends FieldValues,
  TContext,
  TTransformedValues,
  TResult,
>(
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
  mutationFn: (values: TTransformedValues) => Promise<TResult>,
  options: UseFormSubmitOptions<TFieldValues, TTransformedValues, TResult> = {},
): UseFormSubmitReturn {
  const { onSuccess, fieldMap, onError } = options;
  const { t } = useTranslation();
  const [conflict, setConflict] = useState(false);
  const mutation = useMutation<TResult, Error, TTransformedValues>({ mutationFn });

  const onSubmit = form.handleSubmit(async (values) => {
    setConflict(false);
    try {
      const result = await mutation.mutateAsync(values);
      await onSuccess?.(result, values);
    } catch (error) {
      if (error instanceof PreconditionFailedError) {
        setConflict(true);
        return;
      }
      const problem = error instanceof ApiError ? error.problem : undefined;
      let unmappedGlobal: string | null = null;
      if (problem?.errors !== undefined && problem.errors.length > 0) {
        ({ unmappedGlobal } = applyProblemToForm(problem, form.setError, {
          fieldMap,
          knownFields: Object.keys(form.getValues()),
        }));
      }
      if (onError !== undefined) {
        onError(error);
        return;
      }
      surfaceProblem(error, t, { unmappedGlobal });
    }
  });

  const dismissConflict = useCallback(() => {
    setConflict(false);
  }, []);

  return {
    onSubmit,
    isPending: form.formState.isSubmitting || mutation.isPending,
    conflict,
    dismissConflict,
  };
}
